/**
 * Client for the registerWbr GraphQL mutation (UI -> AppSync -> Lambda).
 *
 * Backend flow: React UI -> AppSync `registerWbr` mutation -> register_wbr
 * Lambda -> validate + persist to Aurora PostgreSQL + trigger agent generation.
 *
 * Uses plain fetch against the AppSync endpoint with the Cognito id token
 * (no aws-amplify). Falls back to local validation + a mock result when
 * runtime settings are absent (local dev / tests).
 */
import { getIdToken } from "./auth";
import { getSettings } from "./runtimeConfig";

export interface MetricProfileInput {
  name: string;
  extract_queries: string[];
  metrics: string[];
  report_sections: string[];
}

export interface RegisterWbrInput {
  // wbr_id and team_id are generated by the backend for now, so they are
  // optional on the client. If omitted, register_wbr assigns them.
  wbr_id?: string;
  team_id?: string;
  name?: string;
  metric_profiles: MetricProfileInput[];
  prompt_templates: { name: string; body: string }[];
  skills: { name: string; body: string }[];
  schedule: string;
  recipients: string[];
}

export interface RegisterWbrResult {
  ok: boolean;
  wbr_id?: string;
  team_id?: string;
  request_id?: string;
  errors?: string[];
  message?: string;
}

const REGISTER_WBR_MUTATION = `
  mutation RegisterWbr($input: RegisterWbrInput!) {
    registerWbr(input: $input) {
      ok
      wbr_id
      team_id
      request_id
      errors
      message
    }
  }
`;

/**
 * Mirror of the backend's pure validation so the UI gives immediate feedback
 * and the mock path returns realistic results before the API is reachable.
 */
export function validateInput(input: RegisterWbrInput): string[] {
  const errors: string[] = [];
  // wbr_id / team_id are backend-generated for now, so they are not validated
  // here. If a client does supply them, enforce the length bound.
  const idOk = (v: string) => v.length >= 1 && v.length <= 128;
  if (input.wbr_id !== undefined && !idOk(input.wbr_id))
    errors.push("wbr_id must be 1-128 characters");
  if (input.team_id !== undefined && !idOk(input.team_id))
    errors.push("team_id must be 1-128 characters");

  if (input.metric_profiles.length === 0) {
    errors.push("at least one Metric_Profile is required");
  }
  input.metric_profiles.forEach((p) => {
    if (!p.name.trim()) errors.push("a Metric_Profile is missing a name");
    else {
      if (p.extract_queries.length === 0)
        errors.push(`Metric_Profile '${p.name}' has no extract queries`);
      if (p.report_sections.length === 0)
        errors.push(`Metric_Profile '${p.name}' has no report sections`);
    }
  });

  if (!input.schedule.trim()) errors.push("schedule is required");
  if (input.recipients.length < 1 || input.recipients.length > 100) {
    errors.push("recipients must contain between 1 and 100 entries");
  }
  input.prompt_templates.forEach((pt) => {
    if (!pt.body.trim()) errors.push(`Prompt_Template '${pt.name}' is empty`);
    else if (pt.body.length > 100000)
      errors.push(`Prompt_Template '${pt.name}' exceeds 100000 characters`);
  });
  input.skills.forEach((sk) => {
    if (!sk.body.trim()) errors.push(`Skill '${sk.name}' is empty`);
    else if (sk.body.length > 100000)
      errors.push(`Skill '${sk.name}' exceeds 100000 characters`);
  });
  return errors;
}

function mockRegister(input: RegisterWbrInput): RegisterWbrResult {
  const errors = validateInput(input);
  if (errors.length > 0) {
    return {
      ok: false,
      errors,
      message: "Registration rejected: " + errors.join("; "),
    };
  }
  return {
    ok: true,
    wbr_id: input.wbr_id || "wbr-" + Date.now(),
    team_id: input.team_id || "team-" + Date.now(),
    request_id: "mock-" + Date.now(),
    errors: [],
    message: `WBR '${input.name || "new WBR"}' registered (mock)`,
  };
}

export async function registerWbr(
  input: RegisterWbrInput
): Promise<RegisterWbrResult> {
  const settings = getSettings();

  // No runtime settings → not connected to AppSync yet; validate + mock.
  if (!settings) {
    return mockRegister(input);
  }

  const token = getIdToken();
  const res = await fetch(settings.graphqlEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({
      query: REGISTER_WBR_MUTATION,
      variables: { input },
    }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    return {
      ok: false,
      errors: json.errors.map((e: { message: string }) => e.message),
    };
  }
  return json.data.registerWbr as RegisterWbrResult;
}
